import { ReactElement } from 'react';

import {
  OnboardingTour,
  TOUR_IDS,
  TOUR_PRIORITY,
  type TourStep,
  useOnboardingTour,
} from '@components/OnboardingTour';
import { useMediaQuery, useTheme } from '@mui/material';

import { NOTIFICATION_TOUR_ANCHORS } from '../header/notifications/notificationTourAnchors.js';

const NOTIFICATION_BELL_TOUR_STEPS: TourStep[] = [
  {
    targetId: NOTIFICATION_TOUR_ANCHORS.bell,
    title: 'Your notifications',
    body: 'The bell lists the outcome of the background actions you started, whether they succeeded or failed. If one fails, we also send you an email.',
    placement: 'bottom-end',
  },
];

const NOTIFICATION_AVATAR_TOUR_STEPS: TourStep[] = [
  {
    targetId: NOTIFICATION_TOUR_ANCHORS.avatar,
    title: 'Your notifications',
    body: 'Your notifications are in your account menu: tap your avatar, then Notifications, to see the outcome of the background actions you started, whether they succeeded or failed. If one fails, we also send you an email.',
    placement: 'bottom-end',
  },
];

type NotificationBellOnboardingTourProps = {
  ready?: boolean;
};

type NotificationBellTourProps = Required<NotificationBellOnboardingTourProps> & {
  steps: TourStep[];
};

function NotificationBellTour({ ready, steps }: NotificationBellTourProps): ReactElement {
  const tour = useOnboardingTour(TOUR_IDS.notificationBell, steps, {
    ready,
    priority: TOUR_PRIORITY.notificationBell,
  });

  return (
    <OnboardingTour
      open={tour.open}
      steps={tour.steps}
      onClose={tour.onClose}
      onUnavailable={tour.onUnavailable}
    />
  );
}

export function NotificationBellOnboardingTour({
  ready = true,
}: NotificationBellOnboardingTourProps): ReactElement {
  const theme = useTheme();
  const isDesktopHeader = useMediaQuery(theme.breakpoints.up('md'));
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  if (isPhone) {
    return (
      <NotificationBellTour
        key="header-phone"
        ready={ready}
        steps={NOTIFICATION_AVATAR_TOUR_STEPS}
      />
    );
  }

  return (
    <NotificationBellTour
      key={isDesktopHeader ? 'header' : 'header-mobile'}
      ready={ready}
      steps={NOTIFICATION_BELL_TOUR_STEPS}
    />
  );
}

export default NotificationBellOnboardingTour;
